import * as React from "react";

import "./sample-address.scss";

import {faSpinner}          from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";
import {cx}                 from "emotion";

import {ComponentLink, Link, makeChain} from "@Common/react/cst2";
import {Load2}                          from "@Common/react/load2";
import {AddressModalBase}               from "@Common/ui-components/heavy/mailing-ui/address-modal-base/address-modal-base";

import {consumeContext} from "@Xpresso/loaders/consume-context";

export const SampleAddressSection = ({idEntity="", next: rootNext}:{
    idEntity    ?: string,
    next         : Link<JSX.Element, {
        loading: boolean,
        render: (next:{padded?: boolean, next: Link<JSX.Element, {
            header  : {
                title       : string,
                subtitle   ?: string,
                action      : {
                    onClick     : () => void,
                    label       : string,
                    xdQaName    : string,
                },
            },
            content : React.ReactNode
        }>}) => JSX.Element,
    }>
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then("sampleAddressLoad", ({apis}, next:ComponentLink<typeof Load2<{
        address: Record<string, string>,
        cassError: string | null
    }>>) => (
        Load2({next, fetch: async () =>
        {
            const address = await apis.next.sampleAddress.getSampleAddress(idEntity);
            const cassResult = await apis.next.sampleAddress.cassSampleAddress(address.idPrimary);
            return {address, cassError: cassResult.description ?? null};
        }})
    ))
    .then("sampleAddressModal", ({}, next:ComponentLink<typeof AddressModalBase>) => AddressModalBase({
        next,
        title: "Edit Sample Address",
        fields: [
            {
                path: "name",
                label: "Name",
                required: true,
            },
            {
                path: "name2",
                label: "Name 2",
            },
            {
                path: "address",
                label: "Address",
                required: true,
            },
            {
                path: "address2",
                label: "Address 2",
            },
        ]
    }))
    .then(({
        apis,
        company,
        sampleAddressLoad: {value: sampleAddressMeta, loading, reload: reloadSampleAddress},
        sampleAddressModal,
    }) =>
    {
        const {address: sampleAddress, cassError} = (sampleAddressMeta ?? {});

        const updateAddress = (
            async (address: any) =>
            {
                const result = await apis.next.sampleAddress.saveSampleAddress({
                    ...address,
                    idEntity,
                    sampleAddressId: sampleAddressMeta.address.idPrimary,
                });
                if (result.result === "failure") return {
                    address,
                    validationMessage: `Did not update: ${result.errors.msg}`
                };

                const {address: reloadedAddress, cassError} = await reloadSampleAddress();
                return (
                    !cassError
                    ? undefined
                    : {address: reloadedAddress, validationMessage: `Updated with warning: ${cassError}`}
                );
            }
        );

        return rootNext({
            loading,
            render      : ({padded=true, next}) => next({
                header  : {title: "Sample Address", action: {
                    label       : "Edit",
                    xdQaName    : "Edit Sample Address",
                    onClick     : () => sampleAddressModal.show({
                        actionName  : "edit",
                        countryCode : (
                            ["CA", "CANADA"].includes(company.companyCountry.toUpperCase())
                            ? "CA"
                            : "US"
                        ),
                        initData    : sampleAddress,
                        onConfirm   : updateAddress,
                    })
                }},
                content : (<div className={cx("sample-address-ff433", {padded})}>
                    {loading && <FontAwesomeIcon icon={faSpinner} spinPulse/>}
                    {cassError && <div className="error">Warning: {cassError}</div>}
                    {!loading && <div className="card">
                        <div>{sampleAddress.name}</div>
                        {sampleAddress.name2 && <div>{sampleAddress.name2}</div>}
                        <div>{sampleAddress.address}</div>
                        {sampleAddress.address2 && <div>{sampleAddress.address2}</div>}
                        <div>{sampleAddress.city}, {sampleAddress.state}, {sampleAddress.zip}</div>
                    </div>}
                </div>)
            }),
        });
    })
    .run()
);

